import barba from '@barba/core';
import anime from 'animejs';
import 'nodelist-foreach-polyfill';
import objectFitImages from 'object-fit-images';
import { Accordion } from 'accordion/src/accordion.js';
import svg4everybody from 'svg4everybody';
import scrollbarWidth from 'scrollbar-width';

import toggleHeaderMenu from 'components/header/header';
import imageLazy from 'components/image-lazy/init';
import clientsSection from 'components/clients-section/init';
import carousel from 'components/carousel/init';
import projectsSection from 'components/projects-section/init';
import fullscreenVideo from 'components/fullscreen-video/fullscreen-video';
import contactForm from 'components/contact-form/contact-form';

import blog from './blog';
import projectTransition from './project-transition';
import letters from './letters';
import scrollFadeIn from './scroll-fade-in';
import initScrollTo from './scroll-to';
import { areTransitionsSmooth, debounce } from './helpers';
import movingImage from './moving-image';
import cursor from './cursor';
import smoothScroll from './smooth-scroll';
import captcha from './captcha';
import validate from './components/contact-form/validate';
import formInputs from './components/contact-form/form-inputs';
import headerScrollObserver from './components/header/scroll-observer';

const app = document.querySelector('.app');
const curtain = document.querySelector('.curtain');
let coverListener;
const captchaObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      captcha.protectForm(entry.target);
      captchaObserver.unobserve(entry.target);
    }
  });
});

function initPageDOM(container) {
  const scrollSections = app.querySelectorAll('[data-scroll-section]');
  objectFitImages();
  movingImage.reset();
  imageLazy();
  blog.init(container);
  // projects section should be after letters
  letters(app);
  projectsSection(container);
  clientsSection(container);
  smoothScroll.setSections(scrollSections);
  container.querySelectorAll('[data-scroll-to]').forEach(initScrollTo);
  container.querySelectorAll('[data-accordion]').forEach(el => new Accordion(el, {
    modal: true,
    onToggle: smoothScroll.reflowSections,
  }));
  carousel(container.querySelectorAll('[data-carousel-slider]'));
  container.querySelectorAll('[data-fsv]').forEach(fullscreenVideo);
  container.querySelectorAll('img').forEach(img => img.addEventListener('load', () => {
    blog.refresh();
    setTimeout(smoothScroll.reflowSections, 200);
    setTimeout(smoothScroll.reflowSections, 1000);
  }));
  cursor.toggle(false);
  scrollFadeIn(app);

  const cover = container.querySelector('.cover');
  const fullOnMobile = cover && window.isMobile.any && !cover.classList.contains('cover_short') && !cover.classList.contains('cover_short-mobile');
  if (fullOnMobile) {
    if (coverListener) {
      window.removeEventListener('orientationchange', coverListener);
    }
    const resizeCover = () => {
      cover.style.height = `${window.innerHeight}px`;
    };
    coverListener = debounce(resizeCover, 50);
    window.addEventListener('orientationchange', coverListener);
    resizeCover();
  }

  captchaObserver.disconnect();
  container.querySelectorAll('[data-recaptcha-protected]').forEach(form => captchaObserver.observe(form));

  headerScrollObserver(container);

  formInputs(container);
  validate(container);
}

function fadeIn(container) {
  smoothScroll.instantScrollTo(0);
  try {
    initPageDOM(container);
  } catch (e) {
    console.log(e);
  }
  anime.set(curtain, { opacity: 1, visibility: 'visible', translateY: 0 });
  anime({
    targets: curtain,
    opacity: 0,
    duration: 300,
    delay: 200,
    easing: 'easeOutQuad',
    complete: () => anime.set(curtain, { visibility: 'hidden' }),
  });
}

const transitions = [{
  leave() {
    toggleHeaderMenu(false);
    anime.set(curtain, { visibility: 'visible', opacity: 1, translateY: '100%' });
    return anime({
      targets: curtain,
      translateY: 0,
      duration: 1000,
      easing: 'cubicBezier(.5,0,0,1)',
    }).finished;
  },
  enter(data) {
    fadeIn(data.next.container);
  },
}, {
  from: { namespace: 'projects' },
  to: { namespace: 'projects' },
  leave() {
    return anime({
      targets: '[data-projects-list]',
      duration: 300,
      opacity: 0,
      easing: 'easeOutQuad',
    }).finished;
  },
  enter(data) {
    const { container } = data.next;
    anime.set(container, { opacity: 0 });
    initPageDOM(container);
    smoothScroll.reflowSections();
    anime({
      targets: container,
      delay: 300,
      duration: 300,
      opacity: 1,
      easing: 'easeOutQuad',
    });
  },
}, {
  to: { namespace: 'project' },
  leave: projectTransition.leave,
  enter(data) {
    smoothScroll.instantScrollTo(0);
    initPageDOM(data.next.container);
    projectTransition.enter(data);
  },
}];

document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('.header');
  const barbaContainer = document.querySelector('[data-barba="container"]');
  const width = scrollbarWidth();
  smoothScroll.startup();
  fadeIn(barbaContainer);
  if (areTransitionsSmooth()) {
    barba.init({ transitions, timeout: 20000 });
  }
  document.documentElement.classList.add('dom-loaded');
  document.documentElement.classList.add(window.isMobile.any ? 'mobile' : 'desktop');
  contactForm.init();
  svg4everybody();

  if (width !== 0) {
    /* This code works only if scrollbars are visible. Not on every device! */
    app.style.paddingRight = `${width}px`;
    movingImage.setScrollbarWidth(width);
    contactForm.setScrollbarWidth(width);
    if (header) {
      header.style.paddingRight = `${width}px`;
    }
  }

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-filters-toggle]');
    if (toggle) {
      const nav = toggle.closest('[data-filters-nav]');
      if (nav) {
        nav.classList.toggle('show');
      }
    }
  });

  const progressWrapper = document.querySelector('[data-scroll-progress-wrapper]');
  const progress = document.querySelector('[data-scroll-progress]');
  if (progressWrapper && progress) {
    smoothScroll.onUpdate(({ s, ch, wy }) => {
      const p = s / (ch - wy);
      progress.style.transform = `scale(${p},1)`;
      progressWrapper.style.opacity = (ch - wy - s > 2000) ? 1 : 0;
    });
  }
});

window.addEventListener('load', smoothScroll.reflowSections);
